import api from "@/lib/api";

/**
 * Open an authenticated GET endpoint that returns HTML/PDF as a new browser tab.
 *
 * Background: the simpler `window.open(apiUrl)` approach silently fails in
 * production because the auth cookie is `SameSite=Lax`, which is not sent on
 * cross-site top-level navigations to a different API host. By fetching the
 * payload with our axios client (which carries the Bearer token) and opening
 * it as a Blob URL, the new tab does not need any credentials at all.
 *
 * @param {string} path - api path beginning with "/" (e.g. "/care-plans/123/export")
 * @param {object} opts
 * @param {string} [opts.filename] - suggested filename, used for downloads
 * @param {boolean} [opts.print=true] - append ?print=1 so the server auto-prints
 */
export async function openAuthedPrintable(path, { print = true } = {}) {
  // Open a blank tab synchronously so popup blockers don't bite, then
  // navigate it once the blob is ready. NOTE: we intentionally DO NOT pass
  // 'noopener,noreferrer' — Chrome returns null for window.open() whenever
  // any noopener feature is supplied, which would force the caller to
  // hijack their own tab. Blob URLs are same-origin so this is safe.
  const tab = window.open("about:blank", "_blank");
  try {
    const url = `${path}${path.includes("?") ? "&" : "?"}${print ? "print=1" : ""}`;
    const { data, headers } = await api.get(url, { responseType: "blob" });
    const mime = headers["content-type"] || "text/html";
    const blobUrl = URL.createObjectURL(new Blob([data], { type: mime }));
    if (tab) {
      tab.location.href = blobUrl;
    } else {
      // Popup blocked — fall back to same-tab navigation.
      window.location.href = blobUrl;
    }
    // Revoke after a generous delay so the new tab has time to parse + print.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    if (tab) {
      try {
        tab.document.body.innerText = `Failed to load: ${
          err?.response?.data?.detail || err?.message || "unknown error"
        }`;
      } catch {
        /* tab may already be closed */
      }
    }
    throw err;
  }
}
